import React, { useState, useEffect } from 'react';
import { Activity, History, Clock, CheckCircle2, Play, RefreshCw, AlertCircle, CheckCircle, XCircle, Zap } from 'lucide-react';
import Layout from '../components/Layout';
import StockHealthMonitor from '../components/StockHealthMonitor';
import CryptoHealthMonitorClean from '../components/CryptoHealthMonitorClean';
import PollingStatusMonitor from '../components/PollingStatusMonitor';
import SignalHistoryPanel from '../components/SignalHistoryPanel';
import { dashboardApi } from '../api/dashboard';
import { API_BASE_URL } from '../config';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('today'); // 'today', 'historical', or 'quality'
  const [error, setError] = useState(null);
  const [qualityCheckLoading, setQualityCheckLoading] = useState(false);
  const [qualityCheckData, setQualityCheckData] = useState(null);
  const [lastCheckTime, setLastCheckTime] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Any initial data fetching can go here
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const username = localStorage.getItem('adminUsername');
  const showSignalHistory = username === 'apalshah';

  const tabs = [
    {
      id: 'today',
      label: "Today's Status",
      icon: Clock,
      description: 'Real-time polling status'
    },
    {
      id: 'historical',
      label: 'Historical Health',
      icon: History,
      description: 'Last 7/14/30 days data quality'
    },
    // Signal History — only for apalshah
    ...(showSignalHistory ? [{
      id: 'signals',
      label: 'Signal History',
      icon: Zap,
      description: 'SI computation audit trail'
    }] : []),
  ];

  const runQualityCheck = async () => {
    try {
      setQualityCheckLoading(true);
      setError(null);

      // Initialize processing state for indexes
      // TEMPORARY: Only testing NIFTY50 and SENSEX for now
      const initialIndexes = [
        ...['NIFTY50', 'SENSEX'].map(s => ({ market: 'india', symbol: s, status: 'pending' }))
      ];

      setQualityCheckData({ indexes: initialIndexes, processing: true });

      const response = await fetch(`${API_BASE_URL}/analytics/data-quality-check/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Quality check failed');
      }

      const data = await response.json();

      // Animate through results to show processing
      // Note: crypto is hidden for now
      const allResults = [
        ...data.results.india.map(r => ({ ...r, market: 'india' })),
        ...data.results.us.map(r => ({ ...r, market: 'us' }))
      ];

      // Show processing animation
      for (let i = 0; i < allResults.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 150)); // 150ms delay between each

        const updatedIndexes = initialIndexes.map((idx, index) => {
          if (index < i) {
            return { ...allResults[index], status: 'complete' };
          } else if (index === i) {
            return { ...allResults[index], status: 'processing' };
          }
          return idx;
        });

        setQualityCheckData({
          indexes: updatedIndexes,
          processing: true,
          summary: data
        });
      }

      // Final update with all complete
      const finalIndexes = allResults.map(r => ({ ...r, status: 'complete' }));
      setQualityCheckData({
        indexes: finalIndexes,
        processing: false,
        summary: data
      });

      setLastCheckTime(new Date().toISOString());
    } catch (err) {
      console.error('Error running quality check:', err);
      setError('Failed to run quality check. Please try again.');
    } finally {
      setQualityCheckLoading(false);
    }
  };

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-400 mb-2">Error loading dashboard</p>
            <p className="text-dark-400 text-sm mb-4">{error}</p>
            <button
              onClick={fetchDashboardData}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="space-y-2">
          <div className="flex items-center space-x-3">
            <Activity className="w-8 h-8 text-primary-400" />
            <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          </div>
          <p className="text-dark-300">Monitor data polling and quality across all markets</p>
        </div>

        {/* Tab Navigation */}
        <div className="dashboard-card">
          <div className="flex space-x-2 border-b border-dark-700">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 font-medium transition-all relative ${
                    activeTab === tab.id
                      ? 'text-primary-400'
                      : 'text-dark-400 hover:text-dark-200'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <div className="text-left">
                    <div className="text-sm font-semibold">{tab.label}</div>
                    <div className="text-xs opacity-75">{tab.description}</div>
                  </div>
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-400"></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'today' && (
          <div className="space-y-6">
            {/* Today's Polling Status */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Clock className="w-6 h-6 text-primary-400" />
                <h2 className="text-xl font-semibold text-white">Real-Time Polling Status</h2>
              </div>
              <p className="text-dark-400 text-sm">
                Current status of data collection across all 18 market indexes
              </p>
            </div>

            <PollingStatusMonitor />
          </div>
        )}

        {activeTab === 'historical' && (
          <div className="space-y-6">
            {/* Historical Health Header */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <History className="w-6 h-6 text-primary-400" />
                <h2 className="text-xl font-semibold text-white">Data Quality History</h2>
              </div>
              <p className="text-dark-400 text-sm">
                Historical data quality metrics and issue detection for all indexes
              </p>
            </div>

            {/* All Markets Health Monitors */}
            <StockHealthMonitor market="india" loading={loading} />
            <StockHealthMonitor market="us" loading={loading} />
            <StockHealthMonitor market="asia_pacific" loading={loading} />
            <StockHealthMonitor market="europe" loading={loading} />
            <CryptoHealthMonitorClean loading={loading} />
          </div>
        )}

        {activeTab === 'signals' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Zap className="w-6 h-6 text-primary-400" />
                <h2 className="text-xl font-semibold text-white">Signal History</h2>
              </div>
              <p className="text-dark-400 text-sm">
                Audit trail of Smart Index computations. Detect signal flips where SI changed zone across cron runs for the same trading date.
              </p>
            </div>
            <SignalHistoryPanel />
          </div>
        )}

        {activeTab === 'quality' && (
          <div className="space-y-6">
            {/* Data Quality Check Header */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="w-6 h-6 text-primary-400" />
                    <h2 className="text-xl font-semibold text-white">Data Quality Validation</h2>
                  </div>
                  <p className="text-dark-400 text-sm mt-1">
                    Comprehensive validation of all indexes and technical indicators
                  </p>
                </div>
                {lastCheckTime && (
                  <div className="text-xs text-dark-400">
                    Last check: {new Date(lastCheckTime).toLocaleString()}
                  </div>
                )}
              </div>
            </div>

            {/* Run Check Button */}
            <div className="dashboard-card">
              <div className="flex items-center justify-between p-6">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">Run Complete Data Quality Check</h3>
                  <p className="text-sm text-dark-300">
                    This will validate all indexes across Crypto, India, and US markets for data completeness and accuracy.
                    Checks include: NULL detection, forward-looking bias, TA indicator validation, and data freshness.
                  </p>
                </div>
                <button
                  onClick={runQualityCheck}
                  disabled={qualityCheckLoading}
                  className="ml-6 flex items-center space-x-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-dark-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium whitespace-nowrap"
                >
                  {qualityCheckLoading ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      <span>Checking...</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5" />
                      <span>Run Check</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Validation Rules */}
            <div className="dashboard-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Validation Rules</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-dark-800/50 rounded-lg p-4 border border-dark-700">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-1">Required Data Fields</h4>
                      <p className="text-xs text-dark-300">Validates that all required columns (close, low, high, open) have non-NULL values</p>
                    </div>
                  </div>
                </div>

                <div className="bg-dark-800/50 rounded-lg p-4 border border-dark-700">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-1">Technical Indicators</h4>
                      <p className="text-xs text-dark-300">Checks DRSI, WRSI, MACD, Bollinger Bands, SMA 50/200 (daily/weekly) are calculated after sufficient data points</p>
                    </div>
                  </div>
                </div>

                <div className="bg-dark-800/50 rounded-lg p-4 border border-dark-700">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-1">Smart Index Range</h4>
                      <p className="text-xs text-dark-300">Ensures Smart Index values are within valid range (1-100) for all trading days</p>
                    </div>
                  </div>
                </div>

                <div className="bg-dark-800/50 rounded-lg p-4 border border-dark-700">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-1">Data Freshness</h4>
                      <p className="text-xs text-dark-300">Verifies last data update is within expected timeframe for each market</p>
                    </div>
                  </div>
                </div>

                <div className="bg-dark-800/50 rounded-lg p-4 border border-dark-700">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-1">Price Consistency</h4>
                      <p className="text-xs text-dark-300">Validates High >= Close >= Low for all trading days</p>
                    </div>
                  </div>
                </div>

                <div className="bg-dark-800/50 rounded-lg p-4 border border-dark-700">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-1">Warm-up Periods</h4>
                      <p className="text-xs text-dark-300">Accounts for initial rows: 14 days (DRSI), 200 days (SMA 200D), 200 weeks (SMA 200W)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Live Validation Results */}
            {qualityCheckData && qualityCheckData.indexes && (
              <div className="dashboard-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-white">Validation Status</h3>
                  {qualityCheckData.summary && (
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span className="text-green-400 font-semibold">{qualityCheckData.summary.passed} Passed</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="w-4 h-4 text-yellow-400" />
                        <span className="text-yellow-400 font-semibold">{qualityCheckData.summary.warnings} Warnings</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <XCircle className="w-4 h-4 text-red-400" />
                        <span className="text-red-400 font-semibold">{qualityCheckData.summary.failed} Failed</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Note: Crypto Market section hidden for now */}

                {/* India Market */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-dark-400 uppercase tracking-wide mb-3">India Market</h4>
                  <div className="space-y-2">
                    {qualityCheckData.indexes.filter(idx => idx.market === 'india').map((index, i) => {
                      const validationStatus = index.status;
                      const isComplete = validationStatus === 'complete' || validationStatus === 'pass' || validationStatus === 'warning' || validationStatus === 'fail';
                      const isPassed = validationStatus === 'pass';
                      const isWarning = validationStatus === 'warning';
                      const isFailed = validationStatus === 'fail';

                      return (
                        <div
                          key={i}
                          className={`flex items-center justify-between p-4 rounded-lg transition-all ${
                            validationStatus === 'pending'
                              ? 'bg-dark-800/30 border border-dark-700'
                              : validationStatus === 'processing'
                              ? 'bg-blue-900/20 border border-blue-700 animate-pulse'
                              : isPassed
                              ? 'bg-green-900/10 border border-green-800/30'
                              : isWarning
                              ? 'bg-yellow-900/10 border border-yellow-800/30'
                              : isFailed
                              ? 'bg-red-900/10 border border-red-800/30'
                              : 'bg-dark-800/30 border border-dark-700'
                          }`}
                        >
                          <div className="flex items-center space-x-3 flex-1">
                            {validationStatus === 'pending' ? (
                              <div className="w-5 h-5 rounded-full border-2 border-dark-600"></div>
                            ) : validationStatus === 'processing' ? (
                              <RefreshCw className="w-5 h-5 text-blue-400 animate-spin" />
                            ) : isPassed ? (
                              <CheckCircle className="w-5 h-5 text-green-400" />
                            ) : isWarning ? (
                              <AlertCircle className="w-5 h-5 text-yellow-400" />
                            ) : isFailed ? (
                              <XCircle className="w-5 h-5 text-red-400" />
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-dark-600"></div>
                            )}

                            <div className="flex-1">
                              <div className="text-sm font-semibold text-white">{index.symbol}</div>
                              {isComplete && index.issues && index.issues.length > 0 && (
                                <div className="text-xs text-dark-300 mt-1">
                                  {index.issues.slice(0, 3).join(' • ')}
                                  {index.issues.length > 3 && ` • +${index.issues.length - 3} more`}
                                </div>
                              )}
                              {validationStatus === 'processing' && (
                                <div className="text-xs text-blue-400 mt-1">Validating indicators...</div>
                              )}
                            </div>
                          </div>

                          {isComplete && (
                            <div className="text-xs text-dark-500">
                              {index.records_checked || 0} records
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* US Market */}
                <div>
                  <h4 className="text-sm font-semibold text-dark-400 uppercase tracking-wide mb-3">US Market</h4>
                  <div className="space-y-2">
                    {qualityCheckData.indexes.filter(idx => idx.market === 'us').map((index, i) => {
                      const validationStatus = index.status;
                      const isComplete = validationStatus === 'complete' || validationStatus === 'pass' || validationStatus === 'warning' || validationStatus === 'fail';
                      const isPassed = validationStatus === 'pass';
                      const isWarning = validationStatus === 'warning';
                      const isFailed = validationStatus === 'fail';

                      return (
                        <div
                          key={i}
                          className={`flex items-center justify-between p-4 rounded-lg transition-all ${
                            validationStatus === 'pending'
                              ? 'bg-dark-800/30 border border-dark-700'
                              : validationStatus === 'processing'
                              ? 'bg-blue-900/20 border border-blue-700 animate-pulse'
                              : isPassed
                              ? 'bg-green-900/10 border border-green-800/30'
                              : isWarning
                              ? 'bg-yellow-900/10 border border-yellow-800/30'
                              : isFailed
                              ? 'bg-red-900/10 border border-red-800/30'
                              : 'bg-dark-800/30 border border-dark-700'
                          }`}
                        >
                          <div className="flex items-center space-x-3 flex-1">
                            {validationStatus === 'pending' ? (
                              <div className="w-5 h-5 rounded-full border-2 border-dark-600"></div>
                            ) : validationStatus === 'processing' ? (
                              <RefreshCw className="w-5 h-5 text-blue-400 animate-spin" />
                            ) : isPassed ? (
                              <CheckCircle className="w-5 h-5 text-green-400" />
                            ) : isWarning ? (
                              <AlertCircle className="w-5 h-5 text-yellow-400" />
                            ) : isFailed ? (
                              <XCircle className="w-5 h-5 text-red-400" />
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-dark-600"></div>
                            )}

                            <div className="flex-1">
                              <div className="text-sm font-semibold text-white">{index.symbol}</div>
                              {isComplete && index.issues && index.issues.length > 0 && (
                                <div className="text-xs text-dark-300 mt-1">
                                  {index.issues.slice(0, 3).join(' • ')}
                                  {index.issues.length > 3 && ` • +${index.issues.length - 3} more`}
                                </div>
                              )}
                              {validationStatus === 'processing' && (
                                <div className="text-xs text-blue-400 mt-1">Validating indicators...</div>
                              )}
                            </div>
                          </div>

                          {isComplete && (
                            <div className="text-xs text-dark-500">
                              {index.records_checked || 0} records
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
